import { useState, useEffect, useRef } from 'react'
import { formatDistanceToNowStrict } from 'date-fns'
import { getDateFnsLocale } from '@/locales/date-fns-locales-translation'
import { Search, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getChatThread } from '@/api/workstation'
import { useAuthStore } from '@/stores/authStore'
import { getRoleDetails } from '@/utils/chat-messages-style-config'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import GeneralPagination from '@/components/general-pagination'
import type { OutputMetric } from '@/api/types/workstation.types'
import { useChatContext } from '../../hooks/useChatContext'
import { useChatHistoryQuery } from '../../hooks/useChatQueries'
import { useDeleteChatMutation } from '../../hooks/useChatMutations'
import { Markdown } from '../ask/markdown'
import { tokens } from '@/locales/translations/tokens'
import { debounce, DebouncedFunc } from 'lodash'
import { ChatActions } from './components/chat-actions'
import { ConfirmDialog } from '@/components/confirm-dialog'

interface HistoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function HistoryDialog({ open, onOpenChange }: HistoryDialogProps) {
  const [selectedChat, setSelectedChat] = useState<OutputMetric | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [page, setPage] = useState(0)
  const [debouncedSetFilter, setDebouncedSetFilter] = useState<DebouncedFunc<(value: string) => void>>();
  const [filter, setFilter] = useState('')
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const ITEMS_PER_PAGE = 7

  const { i18n, t } = useTranslation()
  const { handleLoadChat } = useChatContext()
  const user = useAuthStore((state) => state.auth.user)

  const chatHistoryQuery = useChatHistoryQuery({
    page,
    filter,
    itemsPerPage: ITEMS_PER_PAGE,
    enabled: open,
  })
  const deleteChatMutation = useDeleteChatMutation()

  const chats = (chatHistoryQuery.data?.data ?? []) as OutputMetric[]
  const totalCount = chatHistoryQuery.data?.totalCount ?? 0
  const isLoading = chatHistoryQuery.isLoading

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setPage(0)
      setSearchValue('')
      setSelectedChat(null)
    }
    onOpenChange(isOpen)
  }

  const handleChatSelect = (chat: OutputMetric) => {
    setSelectedChat(chat)
  }

  useEffect(() => {
    const debouncer = debounce((value: string) => {
      setFilter(value)
    }, 500);
    setDebouncedSetFilter(() => debouncer);

    return debouncer.cancel();
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    setPage(0);
    if(debouncedSetFilter) {
      debouncedSetFilter(e.target.value)
    }
  }

  const loadChat = async () => {
    if (selectedChat) {
      try {
        const threadData = await getChatThread(selectedChat.chatId)
        if (
          !threadData ||
          !Array.isArray(threadData.messages) ||
          !threadData.messages.length
        ) {
          handleOpenChange(false)
          return
        }

        handleLoadChat(threadData.messages, selectedChat.chatId)
        handleOpenChange(false)
        setTimeout(() => {
          window.scrollTo(0, 0)
        }, 100)
      } catch (_error) {
        handleOpenChange(false)
      }
    }
  }

  useEffect(() => {
    if (scrollAreaRef.current && selectedChat) {
      const scrollContainer = scrollAreaRef.current
      scrollContainer.scrollTop = scrollContainer.scrollHeight
    }
  }, [selectedChat])

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE) || 1

  const handlePageChange = (newPage: number) => {
    setPage(newPage - 1)
  }

  const handleChatDelete = () => {
    if (!chatIdToDelete) return
    deleteChatMutation.mutate(chatIdToDelete, {
      onSettled: () => {
        if (selectedChat?.chatId === chatIdToDelete) {
          setSelectedChat(null)
        }
        setIsDeleteDialogOpen(false)
        setChatIdToDelete(null)
      },
    })
  }

  const renderHistoryContent = () => (
    <div className='flex h-full overflow-hidden rounded-md border'>
      <div
        className='flex w-1/3 flex-col border-r'
        style={{ maxHeight: '100%' }}
      >
        <div className='flex-shrink-0 border-b p-3.5'>
          <div className='relative'>
            <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              type='search'
              placeholder={t(tokens.dashboard.chatHistoryTable.questionFilter.placeholder)}
              className='pl-8'
              value={searchValue}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className='min-h-0 flex-1'>
          <ScrollArea className='h-full pr-1'>
            {isLoading ? (
              <div className='flex h-32 items-center justify-center'>
                <p>{t(tokens.ui.common.loading)}</p>
              </div>
            ) : chats.length === 0 ? (
              <div className='flex h-full flex-1 items-center justify-center'>
                <p>{t(tokens.dashboard.chatHistoryTable.noChatsFound)}</p>
              </div>
            ) : (
              <div className='divide-y'>
                {chats.map((chat) => (
                  <div
                    key={chat.chatId}
                    className={`cursor-pointer p-4 hover:bg-muted/50 ${
                      selectedChat?.chatId === chat.chatId ? 'bg-muted' : ''
                    }`}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center space-x-2'>
                        <MessageSquare className='h-4 w-4 text-muted-foreground' />
                        <span className='max-w-[200px] truncate font-medium'>
                          {chat.firstQuestion || 'Conversa sem título'}
                        </span>
                      </div>
                      <span className='shrink-0 text-xs text-muted-foreground whitespace-nowrap'>
                        {formatDistanceToNowStrict(
                          new Date(chat.createdAt || new Date()),
                          {
                            addSuffix: true,
                            locale: getDateFnsLocale(i18n.language),
                          }
                        )}
                      </span>
                      <ChatActions chatId={chat.chatId} onDelete={(chatId) => {
                        setChatIdToDelete(chatId)
                        setIsDeleteDialogOpen(true)
                      }} />
                    </div>
                    <p className='mt-1 truncate text-xs text-muted-foreground'>
                      {`${chat.userMessagesCount} mensagens`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <div className='flex flex-shrink-0 justify-center border-t p-3'>
          {!isLoading && chats.length > 0 && (
            <GeneralPagination
              currentPage={page + 1}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>

      <div className='flex w-2/3 flex-col'>
        {selectedChat ? (
          <>
            <div className='flex items-center justify-between border-b p-4'>
              <h3 className='max-w-[400px] truncate font-semibold'>
                {selectedChat.firstQuestion || 'Conversa sem título'}
              </h3>
              <Button size='sm' onClick={loadChat}>
                {t(tokens.ui.actions.continue)} chat
              </Button>
            </div>

            <ScrollArea className='flex-1 p-4' ref={scrollAreaRef}>
              <div className='flex flex-col gap-4 pb-4'>
                {selectedChat.messages &&
                  selectedChat.messages.map((msg, index) => {
                    const isUser = msg.role === 'user'
                    const roleDetails = getRoleDetails(
                      msg.role,
                      user?.user_metadata.avatar_url
                    )
                    const formattedTime = formatDistanceToNowStrict(
                      new Date(msg.createdAt || selectedChat.createdAt),
                      {
                        addSuffix: true,
                        locale: getDateFnsLocale(i18n.language),
                      }
                    )

                    return (
                      <div
                        key={index}
                        className={`flex ${isUser ? 'justify-end' : 'justify-start'} gap-3 duration-200 animate-in fade-in`}
                      >
                        {!isUser && (
                          <Avatar className='mt-1 h-8 w-8'>
                            {roleDetails.icon}
                            <AvatarFallback
                              className={`${roleDetails.bgColor} ${roleDetails.textColor}`}
                            >
                              {roleDetails.fallbackIcon}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div
                          className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[80%]`}
                        >
                          <div
                            className={`rounded-2xl px-4 py-3 ${roleDetails.bubbleBg} ${roleDetails.bubbleText} ${roleDetails.bubbleRounded} border border-slate-200 shadow-sm dark:border-slate-700`}
                          >
                            <Markdown>{msg.content}</Markdown>
                          </div>
                          <div className='mt-1 flex items-center gap-2 px-2'>
                            <span className='text-xs text-slate-500 dark:text-slate-400'>
                              {msg.role !== 'user' &&
                                msg.role !== 'assistant' && (
                                  <span className='mr-1 inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[10px] uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-300'>
                                    {msg.role}
                                  </span>
                                )}
                              {formattedTime}
                            </span>
                          </div>
                        </div>

                        {isUser && (
                          <Avatar className='mt-1 h-8 w-8'>
                            {roleDetails.icon}
                            <AvatarFallback className='bg-blue-100 text-blue-700'>
                              {roleDetails.fallbackIcon}
                            </AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                    )
                  })}
                {!selectedChat.messages && (
                  <div className='flex items-center justify-center p-4 text-muted-foreground'>
                    <p>
                      Select a chat
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className='flex flex-1 items-center justify-center text-muted-foreground'>
            <p>{t(tokens.ui.common.selectConversation)}</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='gap-0 overflow-hidden p-0 sm:max-w-[900px] md:max-w-[1000px] lg:max-w-[1200px]'
        style={{ height: '80vh' }}
      >
        <DialogHeader className='border-b px-6 py-4'>
          <DialogTitle>{t(tokens.workstation.history.title)}</DialogTitle>
          <DialogDescription>
            {t(tokens.workstation.history.subtitle)}
          </DialogDescription>
        </DialogHeader>
        <div
          className='flex-1 overflow-hidden'
          style={{ height: 'calc(80vh - 80px)' }}
        >
          {renderHistoryContent()}
        </div>
      </DialogContent>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open)
          if (!open) {
            setChatIdToDelete(null)
          }
        }}
        title={t(tokens.dashboard.chatHistoryTable.deleteDialogTitle)}
        desc={t(tokens.dashboard.chatHistoryTable.deleteDialogDescription)}
        destructive
        isLoading={deleteChatMutation.isPending}
        handleConfirm={handleChatDelete}
        cancelBtnText={t(tokens.ui.actions.cancel)}
        confirmText={t(tokens.ui.actions.delete)}
      />
    </Dialog>
  )
}
